(ns frontend.view.reports.custom.diamond-edge-common
  (:require
   [common.localization :as loc]
   [frontend.state.current-session :as session]
   [frontend.ui.component.select-input.view :as select-input-group]
   [ui.components.date-picker.view :as date-picker]
   [common.dates :as dt]
   [frontend.view.reports.custom.diamond-edge-styles :as style]
   [frontend.ui.component.icon.view :refer [icon]]))

(def default-date-ranges
  [:date-range-all
   :date-range-ltm
   :date-range-ytd
   :date-range-last-30-days
   :date-range-mtd
   :date-range-this-q-to-date
   :date-range-prev-q
   :date-range-q-1
   :date-range-q-2
   :date-range-q-3
   :date-range-q-4
   :date-range-custom])

(def default-date-range-opts
  (->> default-date-ranges
       (mapv (fn [k]
               [k (loc/tr k)]))))

(def range-lookup
  (into {} default-date-range-opts))

(defn js-date->utc-js-date
  [dt]
  (when dt
    (let [ms-value (.getTime dt)
          offset (* 60 1000 (.getTimezoneOffset dt))
          utc-ms (- ms-value offset)]

      ;; zapflow stores all dates in epoch millis 00:00:00.000 in UTC
      (js/Date. utc-ms))))

(defn utc-js-date->local-date
  [dt]
  (when dt
    (let [ms-value (.getTime dt)
          offset (* 60 1000 (.getTimezoneOffset dt))
          utc-ms (+ ms-value offset)
          res (js/Date. utc-ms)]
      (js/Date. utc-ms))))

(defn now [] (js/Date. (js/Date.now)))
(defn end-of-today-utc [] (.setUTCHours (js/Date.) 23 59 59 0))

(defn start-of-day-utc [date] (js/Date. (.setUTCHours date 0 0 0 0)))
(defn end-of-day-utc [date] (js/Date. (.setUTCHours date 23 59 59 0)))


(defn twelve-moths-ago
  []
  (let [d (js/Date. (js/Date.now))
        previous-year (- (.getFullYear d) 1)
        new-date (.setFullYear d previous-year)]
    (js/Date. new-date))
)

(defn beginning-of-year [] (js/Date. (js/Date.UTC (.getFullYear (now)) 0 1 0 0 0 0)))

(defn thirty-days-ago
  []
  (let [now (now)
        minus-thirty (js/Date. (.setDate (js/Date. now) (- (.getDate now) 30)))
        result (-> minus-thirty
                   (.setUTCHours 0 0 0 0)
                   (js/Date.))]
    result))

(defn beginning-of-month
  []
  (js/Date. (js/Date.UTC (.getFullYear (now)) (.getMonth (now)) 1 0 0 0 0)))

(defn get-quarter
  "Returns a list (start-date, end-date)"
  [quarter year]
  (condp = quarter
    1 (list (js/Date. (js/Date.UTC year 0 1 0 0 0)) (js/Date. (js/Date.UTC year 2 31 23 59 59)))
    2 (list (js/Date. (js/Date.UTC year 3 1 0 0 0)) (js/Date. (js/Date.UTC year 5 30 23 59 59)))
    3 (list (js/Date. (js/Date.UTC year 6 1 0 0 0)) (js/Date. (js/Date.UTC year 8 30 23 59 59)))
    4 (list (js/Date. (js/Date.UTC year 9 1 0 0 0)) (js/Date. (js/Date.UTC year 11 31 23 59 59)))
  ))

(defn current-quarter-number
  []
  (let [current-month (.getMonth (now))]
    (cond
      (<= 0 current-month 2) 1
      (<= 3 current-month 5) 2
      (<= 6 current-month 8) 3
      (<= 9 current-month 11) 4)))

(defn get-nth-quarter-of-year
  [nth-quarter]
  (get-quarter nth-quarter (.getFullYear (now))))

(defn get-previous-quarter
  []
  (let [current-q (current-quarter-number)
        current-year (.getFullYear (now))]
    (if (= current-q 1)
      (get-quarter 4 (- current-year 1)) ;; last year's final quarter
      (get-quarter (- current-q 1) current-year))))         ;; Previous quarter this year

(defn this-quarter-to-date
  []
  (let [now (now)
        [start-date _] (get-quarter (current-quarter-number) (.getFullYear now))
        end-date (end-of-day-utc now)]
    [start-date end-date]))

(defn get-start-and-end
  "Returns list (start-date end-date)"
  [time-range]
  (condp = time-range
    :date-range-all (list (js/Date. 2018 8 1) (end-of-day-utc (now)))
    :date-range-ltm (list (twelve-moths-ago) (end-of-day-utc (now)))
    :date-range-ytd (list (beginning-of-year) (end-of-day-utc (now)))
    :date-range-last-30-days (list (thirty-days-ago) (end-of-day-utc (now)))
    :date-range-mtd (list (beginning-of-month) (end-of-day-utc (now)))
    :date-range-this-q-to-date (this-quarter-to-date)
    :date-range-prev-q (get-previous-quarter)
    :date-range-q-1 (get-nth-quarter-of-year 1)
    :date-range-q-2 (get-nth-quarter-of-year 2)
    :date-range-q-3 (get-nth-quarter-of-year 3)
    :date-range-q-4 (get-nth-quarter-of-year 4)
    (list (js/Date. 2018 8 1) (end-of-day-utc (now))))
)

(defn get-filter-default-values
  []
  (let [[start-date end-date] (get-start-and-end :date-range-this-q-to-date)]
    {:date-range :date-range-this-q-to-date
     :start-date start-date
     :end-date   end-date}))

(defn filters
  [state]
  (let [date-range (:date-range @state)]
    [:div
     {:class "flex py-3 px-3"}
     [:div
      {:class "realtive"}
      [:div
       {:class "max-w-[190px]"}
       [:span
        (loc/tr :date-range)]
       [select-input-group/react-select-comp
        {:styles           style/select-style
         :menuPortalTarget (.getElementById js/document "react-select-menu-container")
         :menuPosition     "absolute"
         :options          (map select-input-group/->js-option default-date-range-opts)
         :value            (select-input-group/value-single range-lookup date-range)
         :onChange         (fn [v]
                             (let [clj-opt (js->clj v :keywordize-keys true)
                                   date-range (:value clj-opt)
                                   [start-date end-date] (get-start-and-end date-range)]
                               ;; TODO precalculate start and end date epoch millis and store in :end-date and :start-date
                               (swap! state assoc :date-range date-range)
                               (when-not (= date-range :date-range-custom)
                                 (do
                                   (swap! state assoc :start-date start-date)
                                   (swap! state assoc :end-date end-date)))))}]]

      [:div {:class "flex items-end my-3"}
       [:div {:class "flex flex-col"}
        [:span
         (loc/tr :from-date)]
        [date-picker/view
         {:value     (when (:start-date @state)
                       (.getTime (:start-date @state)))
          :on-change (fn [utc-ms]
                       (let [start-date (js/Date. utc-ms)]
                         (swap! state assoc :start-date start-date)
                         (when (and date-range (not= :date-range-custom date-range))
                           (swap! state assoc :date-range :date-range-custom))))
          :variant   :outline}]]

       [:span {:class "text-base-light-text my-3 mx-4"}
        [icon :long-arrow-right]]

       [:div {:class "flex flex-col"}
        [:span
         (loc/tr :to-date)]
        [date-picker/view
         {:value     (when (:end-date @state)
                       (.getTime (:end-date @state)))
          :on-change (fn [utc-ms]
                       (let [end-date (js/Date. utc-ms)]
                         (swap! state assoc :end-date end-date)
                         (when (and date-range (not= :date-range-custom date-range))
                           (swap! state assoc :date-range :date-range-custom))))
          :variant   :outline}]]]]]))
